/**
 * Piezas compartidas entre el panel de usuarios (admin de cliente) y la
 * consola del super admin (RFC-0024/0025): modal, mensajes de error,
 * medidores de consumo, resumen de plan, acciones por usuario y llaves
 * de API.
 */
import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { AccessDelivery, AdminUser, ApiKey, Entitlements, FeatureKey, LimitKey, UsageSummary } from '../lib/types';

export const ERR_MESSAGES: Record<string, string> = {
  invalid_credentials: 'Correo o contraseña incorrectos.',
  org_suspended: 'La cuenta de tu organización está suspendida. Contacta a TerraKairos.',
  plan_expired: 'El plan de tu organización venció. Contacta a TerraKairos para renovarlo.',
  seat_limit_reached: 'La organización ya usa todos sus asientos. Desactiva una cuenta o amplía el plan.',
  organization_required: 'Indica la organización de la cuenta.',
  organization_not_found: 'Organización no encontrada.',
  invalid_plan: 'Plan inválido o retirado.',
  invalid_name: 'Nombre inválido.',
  missing_credentials: 'Escribe correo y contraseña.',
  rate_limited: 'Demasiados intentos. Espera un minuto.',
  password_min_8: 'La contraseña debe tener al menos 8 caracteres.',
  password_too_long: 'La contraseña es demasiado larga.',
  email_exists: 'Ya existe una cuenta con ese correo.',
  invalid_email: 'Correo inválido.',
  invalid_contact_email: 'Correo de contacto inválido.',
  invalid_seat_limit: 'Número de asientos inválido.',
  invalid_active_until: 'Fecha de vencimiento inválida.',
  quota_exceeded: 'Se agotó el cupo del plan para este mes.',
  feature_not_in_plan: 'El plan no incluye esta función.',
  use_change_password: 'Para tu propia cuenta usa "Cambiar contraseña".',
  cannot_demote_self: 'No puedes quitarte a ti mismo el rol de admin.',
  cannot_deactivate_self: 'No puedes desactivar tu propia cuenta.',
  api_key_limit_reached: 'La organización ya tiene el máximo de llaves activas (10).',
  invalid_rules: 'Algún valor del plan no es válido.',
  invalid_overrides: 'Alguna excepción no es válida.',
  invalid_code: 'Código inválido: mayúsculas, números y guión bajo (2 a 32).',
  plan_exists: 'Ya existe un plan con ese código.',
  forbidden: 'No tienes permiso para esta acción.',
  not_found: 'No encontrado.',
  no_changes: 'No hay cambios que guardar.',
  invalid_token: 'El enlace ya se usó o venció. Pide uno nuevo.',
  mail_not_configured: 'El correo no está configurado: falta la llave de Resend (RESEND_API_KEY).',
  mail_failed: 'Resend rechazó el envío.',
};

/** Resultado de un alta o restablecimiento (RFC-0026). */
export interface DeliveryResult {
  email: string;
  delivery: AccessDelivery;
  tempPassword?: string;
  kind: 'invite' | 'reset';
  /** Cliente, cuando el acceso se dio al crear la organización. */
  org?: string | null;
}

/**
 * Cómo le llegó el acceso a la persona: con correo configurado, un enlace
 * para elegir su contraseña; sin él, una temporal que el admin entrega.
 */
export function DeliveryNotice({ r }: { r: DeliveryResult }) {
  const org = r.org ? <>Cliente <strong>{r.org}</strong> · </> : null;
  if (r.delivery === 'email') {
    return (
      <div className="admin-temp-pw">
        {org}
        {r.kind === 'invite' ? 'Invitación enviada a ' : 'Enlace para elegir contraseña enviado a '}<strong>{r.email}</strong>.
        <span className="auth-hint">
          {r.kind === 'invite'
            ? 'Con él crea su contraseña; vence en 7 días.'
            : 'La contraseña anterior ya no funciona y se cerraron sus sesiones; el enlace vence en 24 horas.'}
        </span>
      </div>
    );
  }
  return (
    <div className="admin-temp-pw">
      {org}
      Contraseña temporal de <strong>{r.email}</strong>: <code>{r.tempPassword}</code>
      <span className="auth-hint">
        No salió correo (no está configurado o falló): cópiala y compártela, no se vuelve a mostrar. Al entrar deberá elegir una nueva.
      </span>
    </div>
  );
}

export function msg(code: string): string {
  return ERR_MESSAGES[code] ?? 'Algo salió mal. Intenta de nuevo.';
}

export function errMsg(err: unknown, fallback: string): string {
  return err instanceof ApiError ? msg(err.code) : fallback;
}

export const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Super admin',
  admin: 'Administrador',
  analyst: 'Analista',
};

/** Etiquetas de las funciones y cupos para quien no carga el catálogo del servidor. */
export const FEATURE_LABELS: Record<FeatureKey, string> = {
  score_breakdown: 'Desglose del score',
  price_history: 'Historial de precios',
  watchlist: 'Propiedades observadas',
  export_csv: 'Exportar a CSV',
  api_access: 'Acceso por API',
};

export const LIMIT_LABELS: Record<LimitKey, { label: string; unit: string; metric?: keyof Omit<UsageSummary, 'period'> }> = {
  property_views_per_month: { label: 'Fichas distintas por mes', unit: 'fichas', metric: 'property_views' },
  watchlist_max: { label: 'Observadas por usuario', unit: 'propiedades' },
  export_rows_per_month: { label: 'Filas exportadas por mes', unit: 'filas', metric: 'export_rows' },
  api_requests_per_month: { label: 'Llamadas de API por mes', unit: 'llamadas', metric: 'api_requests' },
};

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  return Number.isFinite(t) ? new Date(t).toLocaleDateString('es-MX', { timeZone: 'America/Mexico_City' }) : '—';
}

const CDMX_YMD = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City', year: 'numeric', month: '2-digit', day: '2-digit' });

/**
 * Vigencias en hora de Ciudad de México, sin importar dónde esté el
 * navegador: "vence el 31 oct" = 31 oct 23:59:59 CDMX (UTC-6 todo el año
 * desde que México quitó el horario de verano en 2022).
 */
export function cdmxDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  return Number.isFinite(t) ? CDMX_YMD.format(new Date(t)) : '';
}

export function endOfCdmxDayIso(ymd: string): string {
  return new Date(`${ymd}T23:59:59-06:00`).toISOString();
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  return Number.isFinite(t)
    ? new Date(t).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
    : '—';
}

export function fmtNum(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : n.toLocaleString('es-MX');
}

export function fmtMoney(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });
}

/** Días que faltan para una fecha (negativo si ya pasó); null sin fecha. */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.ceil((t - Date.now()) / (24 * 60 * 60 * 1000));
}

export function Modal({ title, onClose, children, closable = true, wide = false }: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  closable?: boolean;
  wide?: boolean;
}) {
  return (
    <div className="modal-overlay" onClick={closable ? onClose : undefined}>
      <div className={`modal-card${wide ? ' wide' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>{title}</h2>
          {closable && <button className="modal-close" onClick={onClose} aria-label="Cerrar">×</button>}
        </div>
        {children}
      </div>
    </div>
  );
}

/** Barra de consumo contra un cupo (null = ilimitado). */
export function UsageMeter({ used, limit, unit }: { used: number; limit: number | null; unit: string }) {
  if (limit === null) {
    return <span className="usage-meter-text">{fmtNum(used)} {unit} · ilimitado</span>;
  }
  const pct = limit === 0 ? 100 : Math.min(100, Math.round((used / limit) * 100));
  const tone = pct >= 100 ? 'bad' : pct >= 80 ? 'warn' : 'ok';
  return (
    <span className="usage-meter">
      <span className="usage-meter-track"><span className={`usage-meter-fill ${tone}`} style={{ width: `${pct}%` }} /></span>
      <span className="usage-meter-text">{fmtNum(used)} de {fmtNum(limit)} {unit}</span>
    </span>
  );
}

/** Qué incluye el plan y cuánto se ha consumido este mes. */
export function PlanSummary({ entitlements, usage }: { entitlements: Entitlements; usage: UsageSummary | null }) {
  return (
    <div className="plan-summary">
      <ul className="plan-features">
        {(Object.keys(FEATURE_LABELS) as FeatureKey[]).map((k) => (
          <li key={k} className={entitlements.features[k] ? 'on' : 'off'}>
            <span aria-hidden>{entitlements.features[k] ? '✓' : '—'}</span> {FEATURE_LABELS[k]}
          </li>
        ))}
      </ul>
      <table className="plan-limits">
        <tbody>
          {(Object.keys(LIMIT_LABELS) as LimitKey[]).map((k) => {
            const def = LIMIT_LABELS[k];
            const limit = entitlements.limits[k];
            // Un cupo mensual en 0 con la función apagada no aporta: no se muestra.
            if (limit === 0 && ((k === 'export_rows_per_month' && !entitlements.features.export_csv) ||
                                (k === 'api_requests_per_month' && !entitlements.features.api_access))) return null;
            return (
              <tr key={k}>
                <td>{def.label}</td>
                <td>
                  {def.metric && usage
                    ? <UsageMeter used={usage[def.metric]} limit={limit} unit={def.unit} />
                    : <span className="usage-meter-text">{limit === null ? 'ilimitado' : `${fmtNum(limit)} ${def.unit}`}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Acciones sobre una cuenta: rol, restablecer contraseña, cerrar
 * sesiones, activar/desactivar y (sólo super admin) mover a otra
 * organización. Las contraseñas temporales se entregan al padre para que
 * las muestre una vez.
 */
export function UserRowActions({ user, isSelf, onChanged, onReset, onError, moveTargets }: {
  user: AdminUser;
  isSelf: boolean;
  onChanged: () => void;
  onReset: (r: DeliveryResult) => void;
  onError: (message: string) => void;
  moveTargets?: Array<{ id: string; name: string }>;
}) {
  const [busy, setBusy] = useState(false);
  if (user.role === 'superadmin') return <span className="auth-hint">—</span>;

  async function run(fn: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    try { await fn(); onChanged(); } catch (err) { onError(errMsg(err, 'No se pudo completar la acción.')); } finally { setBusy(false); }
  }

  return (
    <div className="user-actions">
      <select
        value={user.role}
        disabled={busy || isSelf}
        title={isSelf ? 'No puedes cambiar tu propio rol' : 'Rol de la cuenta'}
        onChange={(e) => void run(async () => { await api.adminUpdateUser(user.id, { role: e.target.value as 'admin' | 'analyst' }); })}
      >
        <option value="analyst">Analista</option>
        <option value="admin">Admin</option>
      </select>
      {!isSelf && (
        <button
          type="button" className="auth-text-btn" disabled={busy}
          title="La contraseña actual deja de servir y se cierran sus sesiones. Le llega un enlace por correo para elegir otra (sin correo configurado, se genera una temporal)."
          onClick={() => {
            if (!window.confirm(`¿Restablecer la contraseña de ${user.email}? La actual dejará de funcionar y se cerrarán sus sesiones.`)) return;
            void run(async () => {
              const r = await api.adminResetPassword(user.id);
              onReset({ email: r.email, delivery: r.delivery, tempPassword: r.tempPassword, kind: 'reset' });
            });
          }}
        >
          Restablecer
        </button>
      )}
      {!isSelf && (
        <button
          type="button" className="auth-text-btn" disabled={busy}
          title="Cierra todas las sesiones abiertas de esta cuenta"
          onClick={() => void run(async () => { await api.adminRevokeSessions(user.id); })}
        >
          Cerrar sesiones
        </button>
      )}
      {!isSelf && (
        <button
          type="button" className="auth-text-btn" disabled={busy}
          onClick={() => void run(async () => { await api.adminUpdateUser(user.id, { active: !user.active }); })}
        >
          {user.active ? 'Desactivar' : 'Activar'}
        </button>
      )}
      {moveTargets && moveTargets.length > 0 && !isSelf && (
        <select
          value="" disabled={busy} title="Mover la cuenta a otra organización"
          onChange={(e) => {
            const target = moveTargets.find((o) => o.id === e.target.value);
            if (!target) return;
            if (!window.confirm(`¿Mover ${user.email} a ${target.name}? Se cerrarán sus sesiones.`)) return;
            void run(async () => { await api.adminUpdateUser(user.id, { organizationId: target.id }); });
          }}
        >
          <option value="">Mover a…</option>
          {moveTargets.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      )}
    </div>
  );
}

/** Llaves de API de una organización (RFC-0025): alta, uso y revocación. */
export function ApiKeysSection({ organizationId }: { organizationId?: string }) {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [limit, setLimit] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await api.apiKeysList(organizationId);
      setKeys(r.keys); setEnabled(r.enabled); setLimit(r.monthlyLimit); setError(null);
    } catch (err) {
      setError(errMsg(err, 'No pudimos cargar las llaves.'));
    }
  }
  useEffect(() => { void load(); }, [organizationId]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(null); setSecret(null);
    try {
      const r = await api.apiKeysCreate(name.trim(), organizationId);
      setSecret(r.secret); setName('');
      await load();
    } catch (err) {
      setError(errMsg(err, 'No pudimos crear la llave.'));
    } finally { setBusy(false); }
  }

  async function revoke(k: ApiKey) {
    if (!window.confirm(`¿Revocar la llave "${k.name}"? Las integraciones que la usan dejarán de funcionar.`)) return;
    try { await api.apiKeysRevoke(k.id); await load(); } catch (err) { setError(errMsg(err, 'No pudimos revocarla.')); }
  }

  const base = api.base;
  const live = keys.filter((k) => !k.revoked_at);
  const table = keys.length > 0 && (
    <table className="admin-table">
      <thead><tr><th>Nombre</th><th>Llave</th><th>Llamadas del mes</th><th>Último uso</th><th></th></tr></thead>
      <tbody>
        {keys.map((k) => (
          <tr key={k.id} className={k.revoked_at ? 'admin-inactive' : ''}>
            <td>
              {k.name}
              {k.created_by_email && (
                <div className="admin-subemail">
                  por {k.created_by_email}
                  {k.created_by_active === false && !k.revoked_at && <strong className="flag flag-warn" style={{ marginLeft: 6 }}>cuenta desactivada</strong>}
                </div>
              )}
            </td>
            <td><code>{k.prefix}…</code></td>
            <td>{fmtNum(k.requests_month ?? 0)}</td>
            <td>{k.revoked_at ? `revocada ${fmtDate(k.revoked_at)}` : fmtDateTime(k.last_used_at)}</td>
            <td>{!k.revoked_at && <button type="button" className="auth-text-btn" onClick={() => void revoke(k)}>Revocar</button>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <section className="admin-section">
      <div className="section-title">Llaves de API</div>
      {!enabled ? (
        <>
          <p className="auth-hint">
            El plan de esta organización no incluye acceso por API.
            {live.length > 0 && ' Las llaves de abajo no funcionan mientras tanto, pero volverían a hacerlo si el plan lo incluye: revoca las que ya no se usen.'}
          </p>
          {live.length > 0 && table}
        </>
      ) : (
        <>
          <p className="auth-hint">
            Para integrar el inventario con sistemas propios. Cada llamada cuenta contra el cupo mensual
            {limit !== null ? ` (${fmtNum(limit)} llamadas)` : ' (ilimitado)'}.
          </p>
          <form className="admin-create-row admin-create-row-2" onSubmit={create}>
            <input type="text" placeholder="Nombre (ej. ERP, BI)" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
            <button type="submit" className="auth-submit" disabled={busy}>{busy ? '…' : 'Crear llave'}</button>
          </form>
          {secret && (
            <div className="admin-temp-pw">
              Llave nueva: <code>{secret}</code>
              <span className="auth-hint">Cópiala ahora: no se vuelve a mostrar. Ejemplo de uso:</span>
              <code className="code-block">curl -H "Authorization: Bearer {secret}" "{base}/v1/properties?cve_ent=14&amp;limit=50"</code>
            </div>
          )}
          {table}
        </>
      )}
      {error && <div className="auth-error">{error}</div>}
    </section>
  );
}
